import {map} from 'rxjs/operators';
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {
    DeviceId,
    ProjectId,
    DeviceSlotId,
    DeviceSlotReservationId,
    UserId,
    MachinePoolId,
    AssetId,
    MachineId,
    ScreenshotId
} from '../../types/ids';
import {Device, deviceFromBackendObject, DevToolsEndpoint} from '../../types/devices/device';
import {AuthService} from "../../services/auth.service";
import {GenericApiResponse} from '../apibase.api';
import { HttpClient } from '@angular/common/http';
import {
    deployableFromBackendObject,
    Deployable
} from '../../types/devices/device-template';
import {DeviceSlot, deviceSlotFromBackendObject} from '../../types/devices/device-slot';
import {BandwidthLimitApi, BandwidthLimitApiName} from '../../types/interfaces/bandwidth-limit.interface';
import {DeviceSlotReservation, deviceSlotReservationFromBackendObject} from '../../types/devices/device-slot-reservation';
import {BrowserSessionInfoDto} from '@webmate-web-core/shared/api/rest/browserSession.restapi';
import {Browser, browserFromBackendObject} from '@webmate-web-core/shared/types/browser';
import {DevicePropertyNames, DeviceRequirements} from '@webmate-web-core/shared/types/devices/device-properties';
import {VehicleOffer} from "@webmate-web-core/shared/types/vehicles";
import {MobileAccessToken, MobileAccessTokenId} from "@webmate-web-core/user-profile/user-profile-mobileaccess.component";
import {
    MachinePool,
    machinePoolFromBackendObject,
    MachinePoolUpdateConfig
} from "@webmate-web-core/shared/types/devices/machine";
import {StartupScript} from '@webmate-web-core/shared/types/startupscript';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {PackageInstallation} from '@webmate-web-core/shared/types/packages/package';

@Injectable({
  providedIn: "root"
})
export class DeviceApi {

    constructor(private http: HttpClient, private auth: AuthService, private webmateOptions: WebmateOptions) {
    }

    getDeviceById(deviceId: DeviceId): Observable<Device> {
        return this.http.get<any>("/device/devices/"+deviceId).pipe(map( response => deviceFromBackendObject(response)));
    }

    getDeviceIdsForProject(project: ProjectId): Observable<DeviceId[]> {
        return this.http.get<Array<DeviceId>>("/projects/"+project+"/device/devices");
    }

    getDevicesForProject(project: ProjectId): Observable<Device[]> {
        return this.http.get<Array<Device>>("/projects/" + project + "/device/devices/full");
    }

    getDeployables(slot: DeviceSlotId): Observable<Deployable[]> {
        return this.http.get<Array<any>>(`/slots/${slot}/deployables`).pipe(map(response => {
            return response.map(t => deployableFromBackendObject(t));
        }));
    }

    getAllDeployablesInProject(projectId: ProjectId): Observable<Deployable[]> {
        return this.http.get<Array<any>>(`/projects/${projectId}/deployables`).pipe(map(response => response.map(r => deployableFromBackendObject(r))));
    }

    getVehicleOffersInProject(projectId: ProjectId): Observable<VehicleOffer[]> {
        return this.http.get<Array<any>>(`/projects/${projectId}/vehicle-offers`).pipe(map(response => {
            return response ?? [];
        }));
    }

    requestDeploymentInProject(projectId: ProjectId, requirements: DeviceRequirements, name: string): Observable<Device> {
        let request = {
            'name': name,
            'deviceRequirements': requirements
        };
        return this.http.post<any>("/projects/" + projectId + "/device/devices", request).pipe(map( response => deviceFromBackendObject(response)));
    }

    requestReleaseOfDevice(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/device/devices/"+deviceId);
    }

    requestReconnectOfDevice(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/device/devices/" + deviceId + "/reconnect", {});
    }

    requestResetOfDevice(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/device/devices/" + deviceId + "/reset", {});
    }

    requestRenameOfDevice(deviceId: DeviceId, name: string): Observable<GenericApiResponse> {
        let body = {
            name: name
        };
        return this.http.post<GenericApiResponse>("/device/devices/" + deviceId + "/rename", body);
    }

    getThumbnailSourceForDevice(d: DeviceId, time?: number): string {
        const currentTime = time ?? new Date().getTime();
        return this.auth.makeAuthorizedUrl("/device/" + d + "/thumbnail?timestamp=" + currentTime);
    }

    requestSnapshotOfDevice(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/device/"+deviceId+"/snapshot", {});
    }

    requestRevertToSnapshot(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/device/"+deviceId+"/revert", {});
    }

    requestRevertToInitial(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/device/devices/" + deviceId + "/reverttoinitial", {});
    }

    requestRedeployment(deviceId: DeviceId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/device/devices/"+deviceId+"/redeploy", {});
    }

    getSlotsForProject(projectId: ProjectId): Observable<DeviceSlot[]> {
        return this.http.get<Array<any>>("/projects/"+projectId+"/slots").pipe(map( slotArr => slotArr.map(s => deviceSlotFromBackendObject(s))));
    }

    getAllSlots(): Observable<DeviceSlot[]> {
        return this.http.get<Array<any>>("/administration/slots").pipe(map( slotArr => slotArr.map(s => deviceSlotFromBackendObject(s))));
    }

    getAllMachinePools(): Observable<MachinePool[]> {
        return this.http.get<Array<any>>("/administration/machinepools").pipe(map( slotArr => slotArr.map(s => machinePoolFromBackendObject(s))));
    }

    getSlotById(slotId: DeviceSlotId): Observable<DeviceSlot> {
        return this.http.get<any>("/slots/"+slotId).pipe(map( s => deviceSlotFromBackendObject(s)));
    }

    updateAutoRedeployment(slotId: DeviceSlotId, projectId: ProjectId, redeployNightly: boolean): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/slots/"+slotId+"/daily_redeployment", {
            projectId: projectId,
            redeployDaily: redeployNightly
        });
    }

    addProjectToSlot(slotId: DeviceSlotId, projectId: ProjectId): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/administration/slots/"+slotId+"/projects/"+projectId, {});
    }

    removeProjectFromSlot(slotId: DeviceSlotId, projectId: ProjectId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/administration/slots/"+slotId+"/projects/"+projectId);
    }

    setProjectsForSlot(slotId: DeviceSlotId, projectIds: ProjectId[]): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>("/administration/slots/"+slotId+"/projects", {
            projectIds: projectIds
        });
    }

    setMachinePoolUpdateConfig(poolId: MachinePoolId, updateConfig: MachinePoolUpdateConfig): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/administration/machinepools/"+poolId+"/updateconfig", updateConfig);
    }

    setMachineAnnotations(machineId: MachineId, machinePoolId: MachinePoolId, msg: string): Observable<GenericApiResponse> {
        let annotation = {msg};
        return this.http.post<GenericApiResponse>("/administration/machinepools/" + machinePoolId + "/machines/" + machineId + "/userannotations", annotation);
    }

    deleteMachineAnnotations(machineId: MachineId, machinePoolId: MachinePoolId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/administration/machinepools/" + machinePoolId + "/machines/" + machineId + "/userannotations");
    }

    setSlotProperties(slotId: DeviceSlotId, name: string): Observable<GenericApiResponse> {
        let tmp = {
            name: name
        };
        return this.http.put<GenericApiResponse>("/administration/slots/"+slotId, tmp);
    }

    setDeviceBandwidth(deviceId: DeviceId, speed: BandwidthLimitApiName): Observable<GenericApiResponse> {
        let tmp = {
            speed: speed
        };
        return this.http.put<GenericApiResponse>("/device/devices/" + deviceId + "/netsim/bandwidth", tmp);
    }

    setSlotPacketLoss(deviceId: DeviceId, loss: BandwidthLimitApiName): Observable<GenericApiResponse> {
        let tmp = {
            loss: loss
        };
        return this.http.put<GenericApiResponse>("/devices/" + deviceId + "/packet-loss", tmp);
    }

    setDisconnected(deviceId: DeviceId, disconnected: boolean): Observable<GenericApiResponse> {
        let tmp = {
            disconnected: disconnected
        };
        return this.http.put<GenericApiResponse>("/device/devices/" + deviceId + "/netsim/disconnect", tmp);
    }

    clearSlot(slotId: DeviceSlotId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/slots/" + slotId);
    }

    getReservationsForSlot(slotId: DeviceSlotId): Observable<DeviceSlotReservation[]> {
        return this.http.get<Array<any>>("/slots/" + slotId + "/reservations").pipe(map(rArr => rArr.map(r => deviceSlotReservationFromBackendObject(r))));
    }

    addNewAbsoluteReservationToSlot(slotId: DeviceSlotId, userId: UserId, projectId: ProjectId, begin: string, end: string): Observable<GenericApiResponse> {
        let absReserv = { userId: userId, slotId: slotId, projectId: projectId, begin: begin, end: end, type: "absolute"};
        return this.http.post<GenericApiResponse>("/slots/reservations", absReserv);
    }

    removeReservationFromSlot(reservId: DeviceSlotReservationId, slotId: DeviceSlotId): Observable<GenericApiResponse>{
        return this.http.delete<GenericApiResponse>("/slots/" + slotId + "/reservations/" + reservId);
    }

    setAppInstallRequirement(deviceId: DeviceId, packageInstallation: PackageInstallation): Observable<GenericApiResponse> {
        return this.imposeDevicePropertyRequirement(deviceId, DevicePropertyNames.InstalledPackages, packageInstallation);
    }

    getBrowserSessionsForDevice(deviceId: DeviceId): Observable<Array<BrowserSessionInfoDto>> {
        return this.http.get<any[]>("/device/devices/" + deviceId + "/browsersessions").pipe(map(sessions => {
            return sessions.map(session => {
                session.browser = browserFromBackendObject(session.browser);
                session.associatedStates = session.associatedStates || [];
                return session as BrowserSessionInfoDto;
            });
        }));
    }

    //////////// Mobile Access ///////////
    createMobileAccessToken(description: string, creator: UserId, expirationTime: DateTime | undefined): Observable<MobileAccessTokenId> {
        const createPostBody = { description, expirationTime };
        return this.http.post<MobileAccessTokenId>(`/device/mobileaccesstokens`, createPostBody);
    }

    fetchMobileAccessTokensForUser(userId: UserId): Observable<MobileAccessToken[]> {
        return this.http.get<MobileAccessToken[]>("/device/mobileaccesstokens");
    }

    fetchMobileAccessTokensForCustomer(): Observable<MobileAccessToken[]> {
        return this.http.get<MobileAccessToken[]>("/device/mobileaccesstokens-forcustomer");
    }

    deleteOrRevokeMobileAccessToken(id: MobileAccessTokenId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/device/mobileaccesstokens/${id}`);
    }

    sendMalfunctionReportOfDevice(deviceId: DeviceId, title: string, message: string, projectId: ProjectId) {
        let deviceMalfunctionReport = {optDeviceId: deviceId, optSlotId: undefined, message: message, projectId: projectId};
        if (title) {
            // The component will hand us an empty string (NOT undefined) when the user doesn't want a title.
            // We have this if-clause so that the backend deserializes it as None instead of Some("").
            deviceMalfunctionReport["optTitle"] = title;
        }
        return this.http.post<GenericApiResponse>(`/device/report`, deviceMalfunctionReport);
    }

    sendMalfunctionReportOfSlot(slotId: DeviceSlotId, title: string, message: string, projectId: ProjectId) {
        let slotMalfunctionReport = {optDeviceId: undefined, optSlotId: slotId, message: message, projectId: projectId};
        if (title) {
            // The component will hand us an empty string (NOT undefined) when the user doesn't want a title.
            // We have this if-clause so that the backend deserializes it as None instead of Some("").
            slotMalfunctionReport["optTitle"] = title;
        }
        return this.http.post<GenericApiResponse>(`/device/report`, slotMalfunctionReport);
    }

    requestDeviceUpdate(slotId: DeviceSlotId, openSTFSerial: string, currentVersion: string, message: string, projectId: ProjectId): Observable<GenericApiResponse> {
        const req = {slotId, openSTFSerial, currentVersion, message, projectId};
        console.warn(req)
        return this.http.post<GenericApiResponse>(`/device/update`, req);
    }

    getScriptsInProject(projectId: ProjectId): Observable<Array<StartupScript>> {
        return this.http.get<StartupScript[]>(`/projects/${projectId}/device/startupscripts`);
    }

    addScriptToSlot(assetId: AssetId, slotId: DeviceSlotId) {
        let msg = {asset: assetId};
        return this.http.post<GenericApiResponse>(`/slots/${slotId}/startupscripts`, msg);
    }

    removeScriptFromSlot(assetId: AssetId, slotId: DeviceSlotId) {
        return this.http.delete<GenericApiResponse>(`/slots/${slotId}/startupscripts/${assetId}`);
    }

    imposeDevicePropertyRequirement(deviceId: DeviceId, property: string, requirement: any): Observable<GenericApiResponse> {
        const propReq = { [property]: requirement };
        return this.http.post<GenericApiResponse>("/device/devices/" + deviceId + "/requirements/propertyRequirement", propReq);
    }

    takeDeviceScreenshot(deviceId: DeviceId): Observable<ScreenshotId> {
        return this.http.post<ScreenshotId>("/device/devices/" + deviceId + "/screenshot", '');
    }


    getDeviceAudioWebRtcSignalingURL(deviceId: DeviceId): string {
        return `/device/devices/${deviceId}/audio-webrtc`;
    }


    downloadSlotInfoAsCSV(): Observable<Blob> {
        return this.http.get("/administration/export/slots", {responseType: 'blob'});
    }

    getDeviceDevToolsEndpoints(deviceId: DeviceId): Observable<DevToolsEndpoint[]> {
        return this.http.get<DevToolsEndpoint[]>(`/device/devices/${deviceId}/devtools-endpoints`);
    }

    getDeviceDevToolsEndpointTargets(deviceId: DeviceId, endpointId: string): Observable<any[]> {
        return this.http.get<any[]>(`/device/devices/${deviceId}/devtools-endpoints/${endpointId}/targets`);
    }

    makeWebkitDevToolsUrl(deviceId: DeviceId, endpointId: string, targetId: string, iosVersion: string): string {
        const websocketUrl = this.getDeviceDevToolsWebsocketUrl(deviceId, endpointId, targetId);
        const authWebsocketUrl = websocketUrl + "?access_token=" + this.auth.getAuthToken();

        const baseUrl = this.webmateOptions.devtoolsWebkitFrontendUrl || 'https://devtools-webkit.webmate.io';
        return `${baseUrl}?iosVersion=${iosVersion}&ws=${authWebsocketUrl}`;
    }

    makeChromiumDevToolsUrl(deviceId: DeviceId, endpointId: string, targetId: string): string {
        const websocketUrl = this.getDeviceDevToolsWebsocketUrl(deviceId, endpointId, targetId, true);
        const authWebsocketUrl = websocketUrl + "?access_token=" + this.auth.getAuthToken();

        const baseUrl = this.webmateOptions.devtoolsChromiumFrontendUrl || 'https://devtools-chrome.webmate.io';
        const wsOrWss = this.webmateOptions.baseApiUri.startsWith("https") ? "wss" : "ws";
        return `${baseUrl}/inspector.html?target=tab&${wsOrWss}=${authWebsocketUrl}`;
    }

    getDeviceDevToolsWebsocketUrl(deviceId: DeviceId, endpointId: string, targetId: string, stripSchema: boolean = false): string {
        const base = this.webmateOptions.websocketUri;
        if (stripSchema) {
            // remove ws:// or wss://
            const index = base.indexOf("://");
            const withoutSchema = base.substring(index + 3);
            return withoutSchema + `/device/devices/${deviceId}/devtools-endpoints/${endpointId}/${targetId}`;
        } else {
            return base + `/device/devices/${deviceId}/devtools-endpoints/${endpointId}/${targetId}`;
        }
    }
}
